import { useState, useEffect, useRef } from 'react';
import Prism from 'prismjs';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-go';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-jsx';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/utils';

const prismTheme = `
.token.comment, .token.prolog, .token.doctype, .token.cdata { color: #8d93a0; }
.token.punctuation { color: #a1a1aa; }
.token.property, .token.tag, .token.boolean, .token.number, .token.constant, .token.symbol { color: #f472b6; }
.token.selector, .token.attr-name, .token.string, .token.char, .token.builtin, .token.inserted { color: #4ade80; }
.token.operator, .token.entity, .token.url, .token.variable { color: #67e8f9; }
.token.atrule, .token.attr-value, .token.keyword { color: #c084fc; }
.token.function, .token.class-name { color: #fbbf24; }
.token.regex, .token.important { color: #fb923c; }
.token.important, .token.bold { font-weight: bold; }
.token.italic { font-style: italic; }
.token.deleted { color: #f87171; }
`;

const styleId = 'prism-docs-theme-shared';

function ensureTheme() {
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = prismTheme;
    document.head.appendChild(style);
  }
}

const prismLangMap: Record<string, string> = {
  curl: 'bash',
  bash: 'bash',
  javascript: 'javascript',
  python: 'python',
  go: 'go',
  json: 'json',
  html: 'markup',
  jsx: 'jsx',
};

export type CodeSnippet = {
  label: string;
  language: string;
  code: string;
};

function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
      className={cn(
        'absolute top-3 right-3 px-2 py-1 rounded-md text-xs font-semibold transition-all',
        copied
          ? 'text-green-400'
          : 'text-zinc-400 hover:text-white bg-white/10 hover:bg-white/20',
      )}
    >
      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </button>
  );
}

export function CodeBlockTabs({ snippets }: { snippets: CodeSnippet[] }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const codeRef = useRef<HTMLElement>(null);
  const active = snippets[activeIdx];

  useEffect(() => {
    ensureTheme();
    if (codeRef.current) {
      try {
        Prism.highlightElement(codeRef.current);
      } catch {
        // fallback
      }
    }
  }, [activeIdx, active.code]);

  const prismLang = prismLangMap[active.language] || 'bash';

  return (
    <div className="relative mb-6 rounded-2xl overflow-hidden border border-[#1C1A15]">
      <div className="flex items-center border-b border-white/10 bg-[#1C1A15] overflow-x-auto">
        {snippets.map((snippet, i) => (
          <button
            key={snippet.label}
            onClick={() => setActiveIdx(i)}
            className={cn(
              'px-3.5 py-2 text-xs font-medium transition-all border-b-2 shrink-0',
              i === activeIdx
                ? 'text-[#FBFAF6] border-[#FBFAF6] bg-white/5'
                : 'text-[#B9B3A5] border-transparent hover:text-[#FBFAF6] hover:bg-white/5',
            )}
          >
            {snippet.label}
          </button>
        ))}
      </div>
      <div className="relative">
        <pre className="bg-[#1C1A15] text-[#FBFAF6] p-4 pr-20 overflow-x-auto text-xs leading-relaxed">
          <code ref={codeRef} className={`language-${prismLang}`}>
            {active.code}
          </code>
        </pre>
        <CopyBtn text={active.code} />
      </div>
    </div>
  );
}

export function HighlightedCode({
  code,
  language = 'bash',
  copyText,
}: {
  code: string;
  language?: string;
  copyText?: string;
}) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    ensureTheme();
    if (ref.current) {
      try {
        Prism.highlightElement(ref.current);
      } catch {
        // fallback
      }
    }
  }, [code, language]);

  const prismLang = prismLangMap[language] || 'bash';

  return (
    <div className="relative mb-6 group">
      <pre className="bg-[#1C1A15] text-[#FBFAF6] p-4 pr-20 rounded-2xl overflow-x-auto text-xs leading-relaxed">
        <code ref={ref} className={`language-${prismLang}`}>
          {code}
        </code>
      </pre>
      <CopyBtn text={copyText || code} />
    </div>
  );
}
